// ═══════════════════════════════════════════════════════════════════════
// 7FCC START CAMPAIGN PAGE ("/start" — for Bri) — SINGLE SOURCE OF CONTENT
// Fully ISOLATED from src/data/content.js ("/ready"), militaryContent.js
// and applyContent.js: editing this file never changes any other page and
// vice versa. Shared visual components are reused; every content block is
// a full copy.
// ═══════════════════════════════════════════════════════════════════════

export const BRAND = {
  name: "7 Figure Closing Club",
  short: "7FCC",
};

export const HERO = {
  eyebrow: "High-Ticket Sales Training",
  headlineLines: [
    "How Regular People Are Making",
    "$15k/Month From Anywhere in the World",
    "With One Valuable Skillset",
  ],
  subhead:
    "Learn the skill businesses already pay for, practice it with real coaching, and see if 7FCC is the right fit for you.",
};

export const VSL = {
  eyebrow: "Watch This First",
  headline: "How does it work?",
  support:
    "Watch this quick breakdown of the training, the skill, and what it takes to succeed.",
  videoLabel: "[VSL VIDEO PLACEHOLDER]",
  caption: "Watch the full video before you answer the questions below.",
  // ── VSL VIDEO SOURCE (single field — swap the URL, nothing else) ──
  videoSrc: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac6d5dcd5f4a8af4397b5c8.mp4",
};

export const OPPORTUNITY = {
  eyebrow: "The Opportunity",
  headline: "Why High-Ticket Sales?",
  intro:
    "Instead of building your own product or business from scratch, high-ticket closers develop a sales skill they can use with companies that already have something to sell.",
  cards: [
    {
      title: "No Business to Build",
      desc: "Focus on developing a valuable sales skill instead of creating your own product, fulfillment, or company from scratch.",
    },
    {
      title: "No Large Following Required",
      desc: "You don't need thousands of followers or a massive personal network to learn the skill.",
    },
    {
      title: "Work From Anywhere",
      desc: "High-ticket sales can be performed remotely for many online businesses with a laptop, phone, and internet connection.",
    },
  ],
  steps: [
    { title: "Learn the skill", desc: "Develop the fundamentals of high-ticket sales." },
    { title: "Get on an offer", desc: "Use the skill selling an existing company's product or service." },
    { title: "Earn commission", desc: "Get compensated based on sales performance." },
  ],
};

export const PROOF = {
  headline: "Real Students. Real Progress.",
  support:
    "A few examples of what happens when students put the training into practice.",
  items: [
    {
      src: "/proof/proof-01.png",
      alt: "Facebook post from Joseph Ortega congratulating Salvador on closing a $10K deal",
    },
    {
      src: "/proof/proof-02.png",
      alt: "Message from Betza Rojas: 10K paid in full — 'too easy'",
    },
    {
      src: "/proof/proof-03.png",
      alt: "Team chat showing a $5K paid-in-full close and a $5,000 succeeded payment confirmation",
    },
    {
      src: "/proof/proof-04.png",
      alt: "Daily revenue board showing $41,000 in total with top setters and closers",
    },
    {
      src: "/proof/proof-05.png",
      alt: "Facebook post celebrating Mark's first $100+ win and Sarah for setting the appointment",
    },
    {
      src: "/proof/proof-06.png",
      alt: "Team chat celebrating the top closer of the week",
    },
    {
      src: "/proof/proof-07.png",
      alt: "Facebook post from Elijah Allah — collected $13,500 cash in a day as a closer",
    },
    {
      src: "/proof/proof-08.png",
      alt: "Team chat celebrating a $3,325 payment made within five days",
    },
  ],
  featured: {
    src: "/proof/proof-featured.png",
    alt: "Facebook post announcing Brittany's Elite Closer Certification after generating over $200K in cash collected",
    name: "Brittany",
    result: "$200K+ Collected In Under Two Months",
    text:
      "\u201CCame into this program less than two months ago with zero closing experience and has generated over $200K in cash collected as a closer. She has now received her credentials as a certified closer.\u201D",
  },
};

// Vertical video testimonials — add to, remove from or reorder this list
// freely; the page renders whatever is here, top to bottom.
export const VIDEO_TESTIMONIALS_CAPTION =
  "Don't take our word for it. Hear it from the students putting in the reps.";

// caption is optional (name + one-line result). Empty string = render nothing.
export const TESTIMONIAL_VIDEOS = {
  romy: {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac42ce77d735ea2a14a21ba.mp4",
    alt: "Student video testimonial 1",
    caption: "",
  },
  hayne: {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac44a69195f9172ed994186.mp4",
    alt: "Student video testimonial 2",
    caption: "",
  },
  jackson: {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac44a69195f9172ed99417b.mp4",
    alt: "Student video testimonial 3",
    caption: "",
  },
  ryleigh: {
    src: "https://assets.cdn.filesafe.space/CscG0b1eU7e07D3fczKL/media/6ac42ce7b12649a51231507c.mp4",
    alt: "Student video testimonial 4",
    caption: "",
  },
};

// Placement slot (array of TESTIMONIAL_VIDEOS keys, in display order).
export const WINS_VIDEOS = ["romy", "hayne", "jackson", "ryleigh"];

// Survey section copy. To swap surveys later, change ONLY the src/id here.
export const QUIZ = {
  eyebrow: "Your Next Step",
  headline: "Is this a good fit for you?",
  support:
    "Answer a few quick questions so we can understand where you're at and what you're looking for.",
};

export const SURVEY = {
  src: "https://api.leadconnectorhq.com/widget/survey/hkkD95mCeopty9fK2nrG",
  iframeId: "hkkD95mCeopty9fK2nrG",
  title: "7FCC Qualification Survey",
};

export const FAQ = {
  heading: "Questions before you apply?",
  items: [
    {
      q: "Do I need sales experience?",
      a: "No. Some students start from zero and others come in with experience. The training is designed to build the fundamentals and improve through practice.",
    },
    {
      q: "Is this just an online course?",
      a: "No. 7FCC combines training with coaching, roleplay, repetition and community so students can actually practice the skill.",
    },
    {
      q: "Can I do this remotely?",
      a: "Yes. The training and community are built for remote participation.",
    },
    {
      q: "What happens after I apply?",
      a: "We'll review where you're at and what you're trying to accomplish. If it makes sense to continue, you'll receive the next step to speak with the team.",
    },
  ],
};

export const CTA = {
  label: "See If You Qualify",
  targetId: "qualify",
  note: "60-second application. No sales experience required.",
};

export const FOOTER = {
  links: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms", href: "#" },
    { label: "Contact", href: "#" },
  ],
  copyright: "Copyright © 7FCC",
};

// Social-proof widget demo data (temporary, same as /ready's).
export const ACTIVITY_NOTIFICATIONS = [
  { firstName: "Jessica", location: "Jacksonville, FL", action: "just booked her Success Call" },
  { firstName: "Marcus", location: "Dallas, TX", action: "just applied to 7FCC" },
  { firstName: "Ashley", location: "Phoenix, AZ", action: "just booked a call with the 7FCC team" },
  { firstName: "Chris", location: "Orlando, FL", action: "just submitted an application" },
  { firstName: "Taylor", location: "Austin, TX", action: "just booked a Success Call" },
  { firstName: "Brianna", location: "Miami, FL", action: "just submitted an application" },
  { firstName: "Jordan", location: "Atlanta, GA", action: "just booked his Success Call" },
  { firstName: "Kevin", location: "Chicago, IL", action: "just applied to 7FCC" },
  { firstName: "Samantha", location: "Denver, CO", action: "just booked a call with the 7FCC team" },
  { firstName: "Tyler", location: "Nashville, TN", action: "just booked her Success Call" },
  { firstName: "Brittany", location: "Charlotte, NC", action: "just submitted an application" },
  { firstName: "Andre", location: "Houston, TX", action: "just booked a Success Call" },
  { firstName: "Megan", location: "Portland, OR", action: "just applied to 7FCC" },
  { firstName: "Devonte", location: "Detroit, MI", action: "just booked a call with the 7FCC team" },
  { firstName: "Kayla", location: "Tampa, FL", action: "just submitted an application" },
  { firstName: "Robert", location: "Phoenix, AZ", action: "just booked his Success Call" },
  { firstName: "Jasmine", location: "Newark, NJ", action: "just applied to 7FCC" },
  { firstName: "Cody", location: "Boise, ID", action: "just submitted an application" },
  { firstName: "Alexis", location: "San Diego, CA", action: "just booked a call with the 7FCC team" },
  { firstName: "Malik", location: "Philadelphia, PA", action: "just booked her Success Call" },
  { firstName: "Heather", location: "Columbus, OH", action: "just submitted an application" },
  { firstName: "Sean", location: "Boston, MA", action: "just applied to 7FCC" },
  { firstName: "Victoria", location: "Las Vegas, NV", action: "just booked a Success Call" },
  { firstName: "Isaiah", location: "Memphis, TN", action: "just booked a call with the 7FCC team" },
  { firstName: "Rachel", location: "Minneapolis, MN", action: "just submitted an application" },
  { firstName: "Eric", location: "Salt Lake City, UT", action: "just booked his Success Call" },
  { firstName: "Lauren", location: "St. Louis, MO", action: "just applied to 7FCC" },
  { firstName: "Jamal", location: "Baltimore, MD", action: "just booked a call with the 7FCC team" },
  { firstName: "Brooke", location: "Indianapolis, IN", action: "just submitted an application" },
  { firstName: "Trevor", location: "Kansas City, MO", action: "just booked her Success Call" },
  { firstName: "Monica", location: "El Paso, TX", action: "just applied to 7FCC" },
  { firstName: "Darius", location: "Cleveland, OH", action: "just submitted an application" },
  { firstName: "Stephanie", location: "Tucson, AZ", action: "just booked a Success Call" },
  { firstName: "Blake", location: "Oklahoma City, OK", action: "just booked a call with the 7FCC team" },
  { firstName: "Amber", location: "Pittsburgh, PA", action: "just submitted an application" },
];
